import { useEffect, useMemo, useState } from "react";

import { orderBy, sumBy } from "lodash";
import moment from "moment";

import { getWeeksForDateRange } from "@features/mmm/utils/get_cycle_weeks";
import {
  type ForecastVariables,
  type FetchUniversalMMMData_me_mmm_alerts,
} from "@north-beam/nb-client-side";
import { useAlerts } from "@pages/mmm/hooks/use-alerts";
import { useClientDataColumns } from "@pages/mmm/hooks/use-client-data-columns";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { usePerformanceTrackingSpendData } from "../../performance-tracking/hooks/use-performance-tracking-spend-data";
import { useTableSort } from "../hooks/use-table-sort";

import {
  MidCycleRebudgetingTable,
  type RebudgetingTableRow,
  WeekStatus,
} from "./components/mid-cycle-rebudgeting-table";
import { sortControl } from "./components/table-sorter";

type WeeklyMetrics = {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  actual: number | null;
  budgeted: number | null;
}[];

function getWeekStatusForDate(
  weekStart: moment.Moment,
  weekEnd: moment.Moment,
  referenceDate: moment.Moment,
): WeekStatus {
  if (weekEnd.isBefore(referenceDate)) {
    return WeekStatus.COMPLETE;
  }
  if (weekStart.isSameOrBefore(referenceDate)) {
    return WeekStatus.CURRENT;
  } else {
    return WeekStatus.UPCOMING;
  }
}

const aggregateWeeklyMetrics = (
  rows: {
    weeklyMetrics: WeeklyMetrics;
    [key: string]: any;
  }[],
  weeksDiff: number,
) =>
  Array.from({ length: weeksDiff }).map((_, i) => {
    const weekSum = {
      actual: 0,
      budgeted: 0,
      dateRange: {
        startDate: "",
        endDate: "",
      },
      status: WeekStatus.COMPLETE,
    };
    const today = moment().startOf("day");

    rows.forEach((row) => {
      const week = row.weeklyMetrics[i];
      weekSum.actual += week.actual ?? 0;
      weekSum.budgeted += week.budgeted ?? 0;
      weekSum.dateRange = week.dateRange;
      const weekEnd = moment(weekSum.dateRange.endDate);
      const weekStart = moment(weekSum.dateRange.startDate);
      weekSum.status = getWeekStatusForDate(weekStart, weekEnd, today);
    });

    return weekSum;
  });

export function BudgetAllocationTable({
  getForecast,
  allowForecastAdoption,
  onAdoptForecast,
  forecastError,
}: {
  getForecast: (forecastInput: ForecastVariables) => Promise<unknown>;
  allowForecastAdoption: boolean;
  onAdoptForecast: () => Promise<void>;
  forecastError: boolean;
}) {
  const { trackingSpendData, loading } = usePerformanceTrackingSpendData();
  const [rows, setRows] = useState<RebudgetingTableRow[]>([]);

  const { getCycleDataColumnByColumnName } = useClientDataColumns();

  const [sortKey] = useTableSort();
  const { selectedCycle } = useSelectedCycle();

  const [alerts] = useAlerts();

  const cycleAlerts = useMemo(
    () =>
      alerts.filter(
        (alert) =>
          alert.cycle === selectedCycle?.id && alert.dismissed_at === null,
      ),
    [alerts, selectedCycle?.id],
  );

  const affectedDataColumnsAlertMapping = useMemo(
    () =>
      cycleAlerts.reduce((acc, alert) => {
        const affectedDatacolumns = alert.model_datacolumns_affected;
        affectedDatacolumns.forEach((dataColumnName) => {
          if (acc[dataColumnName] === undefined) {
            acc[dataColumnName] = [alert];
          } else {
            acc[dataColumnName].push(alert);
          }
        });

        return acc;
      }, {} as Record<string, FetchUniversalMMMData_me_mmm_alerts[]>),
    [cycleAlerts],
  );

  const weeks = useMemo(
    () => getWeeksForDateRange(selectedCycle.date_range),
    [selectedCycle.date_range],
  );

  const trackingSpendDataWeekly = useMemo(() => {
    if (loading) {
      return [];
    }
    return trackingSpendData.map((datum) => {
      const weeklyMetricsByStart: Record<string, WeeklyMetrics[number]> = {};
      datum.dailyMetrics.forEach((dailydatum) => {
        // get week
        const weekStart = weeks.find(
          (week) =>
            moment(dailydatum.date).isSameOrAfter(week.startDate) &&
            moment(dailydatum.date).isSameOrBefore(week.endDate),
        );
        if (!weekStart) {
          console.error(
            `Daily datum in tracking page not present: ${dailydatum.date}`,
          );
          return;
        }
        const start = weekStart.startDate;
        if (!weeklyMetricsByStart[start]) {
          weeklyMetricsByStart[start] = {
            dateRange: weekStart,
            actual: 0,
            budgeted: 0,
          };
        }

        weeklyMetricsByStart[start].budgeted! += dailydatum.budgeted ?? 0;
        weeklyMetricsByStart[start].actual! += dailydatum.actual ?? 0;
      });

      const ordered = orderBy(
        Object.values(weeklyMetricsByStart),
        "dateRange.startDate",
        "asc",
      );
      return {
        ...datum,
        weeklyMetrics: ordered,
      };
    });
  }, [trackingSpendData, weeks, loading]);

  const firstBreakdown = selectedCycle.breakdowns[0];

  useEffect(() => {
    if (loading) {
      return;
    }
    const tableRows = trackingSpendDataWeekly.map((datum, index) => {
      const today = moment().endOf("day");

      const {
        key,
        actualSpend,
        actualSpendMix,
        budgetedSpendMix,
        budgetedSpend,
        mtaRevenue,
        mtaTransactions,
        dataColumnName,
      } = datum;

      let budgetedSpendToDate = 0;
      datum.dailyMetrics.forEach((dailyMetric) => {
        if (moment(dailyMetric.date).isSameOrBefore(today)) {
          budgetedSpendToDate += dailyMetric.budgeted ?? 0;
        }
      });

      const dataColumnFriendlyName =
        getCycleDataColumnByColumnName(dataColumnName)?.friendly_name;
      return {
        id: key,
        key,
        index,
        data: {
          weeklyMetrics: datum.weeklyMetrics.map((metric) => {
            const status = getWeekStatusForDate(
              moment(metric.dateRange.startDate),
              moment(metric.dateRange.endDate),
              today,
            );
            return {
              ...metric,
              status,
            };
          }),
          actualSpend,
          actualSpendMix,
          budgetedSpend,
          budgetedSpendMix,
          budgetedSpendToDate,
          mtaRevenue,
          mtaTransactions,
          dataColumnName,
          proposedBudgetedSpend: budgetedSpend,
          mtaRoas: mtaRevenue / actualSpend,
          mtaCac: actualSpend / mtaTransactions,
        },
        metadata: {
          breakdowns: datum.breakdowns,
          currentBreakdown: datum.breakdowns,
          firstBreakdown,
          hasAlert: !!affectedDataColumnsAlertMapping[dataColumnName],
        },
        displayName: dataColumnFriendlyName ?? "ERROR UNKNOWN DATACOLUMN",
        rowType: "normal" as const,
        hasChildren: false,
        children: [],
      };
    });

    const newRows = sortControl(sortKey)(tableRows);
    setRows(newRows.map((row, index) => ({ ...row, index })));
  }, [
    trackingSpendDataWeekly,
    sortKey,
    firstBreakdown,
    loading,
    getCycleDataColumnByColumnName,
    affectedDataColumnsAlertMapping,
  ]);

  const HEADING_ROW = useMemo<RebudgetingTableRow>(() => {
    const actualSpend = sumBy(rows, (e) => e.data.actualSpend);
    const mtaTransactions = sumBy(rows, (e) => e.data.mtaTransactions);
    const mtaRevenue = sumBy(rows, (e) => e.data.mtaRevenue);

    let budgetedSpendToDate = 0;
    rows.forEach((row) => {
      budgetedSpendToDate += row.data.budgetedSpendToDate ?? 0;
    });

    return {
      id: "heading",
      key: "heading",
      isHeaderRow: true,
      index: -1,
      data: {
        weeklyMetrics: aggregateWeeklyMetrics(
          rows.map((e) => e.data),
          weeks.length,
        ),
        actualSpend,
        actualSpendMix: sumBy(rows, (e) => e.data.actualSpendMix),
        budgetedSpend: sumBy(rows, (e) => e.data.budgetedSpend),
        budgetedSpendMix: sumBy(rows, (e) => e.data.budgetedSpendMix),
        proposedBudgetedSpend: sumBy(rows, (e) => e.data.proposedBudgetedSpend),
        budgetedSpendToDate: budgetedSpendToDate,
        mtaRevenue,
        mtaTransactions,
        mtaRoas: mtaRevenue / actualSpend,
        mtaCac: actualSpend / mtaTransactions,
        dataColumnName: "",
      },
      metadata: {
        breakdowns: {},
        currentBreakdown: { key: "", value: "" },
        firstBreakdown: "",
        hasAlert: false,
      },
      displayName: "Aggregations",
      children: [],
      hasChildren: false,
      rowType: "normal",
    };
  }, [rows, weeks.length]);

  return (
    <MidCycleRebudgetingTable
      headingRow={HEADING_ROW}
      loading={loading}
      rows={rows}
      setRows={setRows}
      getForecast={getForecast}
      allowForecastAdoption={allowForecastAdoption}
      onAdoptForecast={onAdoptForecast}
      forecastError={forecastError}
    />
  );
}
